import React, { useState, useMemo, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useAvailability } from '../hooks/useAvailability';
import { createBooking, blockSpot, calcolaPrezzo, cancelBookingAndRelease, getOrdersByBeachAndDate, getAreaPrenotazioni } from '../services/firebase';
import Topbar from '../components/layout/Topbar';
import { Panel, PanelHead, PanelBody, Spinner, Button } from '../components/ui';
import { useWindowSize } from '../hooks/useWindowSize';

const LETTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
const WEEKDAYS = ['Dom', 'Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab'];
const WEEKDAYS_FULL = ['Domenica', 'Lunedì', 'Martedì', 'Mercoledì', 'Giovedì', 'Venerdì', 'Sabato'];
const MONTHS = ['Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno', 'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'];

const STATUS_STYLE = {
  free: { bg: '#ffffff', border: 'rgba(200,185,160,0.7)' },
  occupied: { bg: 'rgba(224,100,60,0.18)', border: 'rgba(224,100,60,0.45)' },
  reserved: { bg: '#ffffff', border: 'rgba(245,200,66,0.8)' },
  blocked: { bg: 'rgba(160,140,100,0.3)', border: 'rgba(130,110,70,0.45)' },
};

// Mappa icone statiche per tipo cella (decorazioni) e fallback per servizi legacy
const ICON_MAP_STATIC = {
  ombrellone: 'beach_access',
  lettino: 'bedroom_child',
  gazebo: 'holiday_village',
  bar: 'local_bar',
  docce: 'shower',
  wc: 'wc',
  bagnino: 'kayaking',
  piscina: 'pool',
  albero: 'park',
  parcheggio: 'local_parking',
  percorso: 'footprint',
};

// Lookup icona con fallback multipli:
// 1. srv.iconaId (configurato dal gestore)
// 2. ICON_MAP_STATIC[cell.type] (tipo statico es. "ombrellone")
// 3. ICON_MAP_STATIC per label match (servizi legacy con id diverso)
function resolveIcon(cell, srv) {
  if (srv?.iconaId) return srv.iconaId;
  if (ICON_MAP_STATIC[cell.type]) return ICON_MAP_STATIC[cell.type];
  // Fallback per label: "Lettino" → bedroom_child, "Ombrellone" → beach_access
  const label = (cell.servizioLabel || srv?.label || '').toLowerCase();
  if (label.includes('ombrellone')) return 'beach_access';
  if (label.includes('lettino')) return 'bedroom_child';
  if (label.includes('gazebo')) return 'holiday_village';
  if (label.includes('bar')) return 'local_bar';
  if (label.includes('docce')) return 'shower';
  if (label.includes('wc')) return 'wc';
  if (label.includes('bagnino')) return 'kayaking';
  if (label.includes('piscina')) return 'pool';
  if (label.includes('albero')) return 'park';
  if (label.includes('parcheggio')) return 'local_parking';
  return null;
}

// Risolve il colore del servizio con fallback
function resolveColore(cell, srv) {
  if (srv?.colore) return srv.colore;
  if (cell.colore) return cell.colore;
  return '#1f93e0';
}

const DATE_TYPES = [
  { id: 'single', label: 'Giorno singolo', icon: '📅' },
  { id: 'range', label: 'Periodo continuativo', icon: '📆' },
  { id: 'multiple', label: 'Date non consecutive', icon: '🗓️' },
  { id: 'seasonal', label: 'Stagionale (giorni sett.)', icon: '🌊' },
];

export default function BeachMapPage() {
  const { admin, beach, tariffe } = useAuth();
  const { isMobile } = useWindowSize();
  const today = new Date().toISOString().split('T')[0];

  const [selectedDate, setSelectedDate] = useState(today);
  const [viewMonth, setViewMonth] = useState(() => {
    const d = new Date();
    return { year: d.getFullYear(), month: d.getMonth() };
  });

  const { availabilityMap, loading, getStatsForDate, refetch } = useAvailability(admin?.beachCode);

  const [drawerCell, setDrawerCell] = useState(null);
  const [drawerMode, setDrawerMode] = useState(null);
  const [extraSel, setExtraSel] = useState({});  // { [extraId]: qty }
  const [saving, setSaving] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [relatedOrder, setRelatedOrder] = useState(null);
  const [loadingOrder, setLoadingOrder] = useState(false);

  // Stato per aree lettini
  const [areaOrders, setAreaOrders] = useState([]);
  const [areaPrenotazioni, setAreaPrenotazioni] = useState({}); // { [srvId]: qty }       // ordini attivi per l'area
  const [loadingArea, setLoadingArea] = useState(false);
  const [dailyOrders, setDailyOrders] = useState([]);
  const [loadingDailyOrders, setLoadingDailyOrders] = useState(false);
  const [areaQty, setAreaQty] = useState(1);              // quantità da prenotare

  const [form, setForm] = useState({
    guestName: '', guestPhone: '', notes: '',
    dateType: 'single',
    dateRange: { dal: today, al: today },
    dates: [], seasonalDays: [], totalPrice: '',
  });

  const setF = (k) => (e) => setForm(p => ({ ...p, [k]: e.target.value }));

  const daysInMonth = useMemo(() => {
    const { year, month } = viewMonth;
    const count = new Date(year, month + 1, 0).getDate();
    return Array.from({ length: count }, (_, i) => {
      const d = new Date(year, month, i + 1);
      return {
        dateStr: `${year}-${String(month + 1).padStart(2, '0')}-${String(i + 1).padStart(2, '0')}`,
        day: i + 1,
        weekday: WEEKDAYS[d.getDay()],
      };
    });
  }, [viewMonth]);

  const prevMonth = () => setViewMonth(({ year, month }) =>
    month === 0 ? { year: year - 1, month: 11 } : { year, month: month - 1 });
  const nextMonth = () => setViewMonth(({ year, month }) =>
    month === 11 ? { year: year + 1, month: 0 } : { year, month: month + 1 });

  const layoutDoc = availabilityMap[selectedDate];
  const layoutCelle = useMemo(() => layoutDoc?.layout || [], [layoutDoc]);
  const servizi = useMemo(() => beach?.servizi || [], [beach?.servizi]);

  const rows = useMemo(() => {
    const map = {};
    layoutCelle.forEach(cell => {
      if (!map[cell.row]) map[cell.row] = [];
      map[cell.row].push(cell);
    });
    return Object.keys(map).map(Number).sort((a, b) => a - b).map(rowIdx => ({
      rowIdx, label: LETTERS[rowIdx],
      cells: map[rowIdx].sort((a, b) => a.col - b.col),
    }));
  }, [layoutCelle]);

  const stats = getStatsForDate(selectedDate);

  const prezzoSuggerito = useMemo(() => {
    if (!drawerCell || !tariffe?.length) return null;
    const s = servizi.find(s => s.id === drawerCell.type);
    if (!s) return null;
    return calcolaPrezzo(tariffe, selectedDate, drawerCell.type, drawerCell.row, s.haFila);
  }, [drawerCell, tariffe, selectedDate, servizi]);

  // Ricalcola totalPrice quando cambiano extra selezionati o il prezzo suggerito
  useEffect(() => {
    if (drawerMode !== 'book' || !servizio) return;
    const base = prezzoSuggerito ?? servizio.prezzo ?? 0;
    const extraTot = (servizio.extra || [])
      .filter(e => e.attivo)
      .reduce((sum, e) => sum + (extraSel[e.id] || 0) * e.prezzo, 0);
    setForm(p => ({ ...p, totalPrice: String(base + extraTot) }));
  }, [extraSel, prezzoSuggerito, drawerMode]); // eslint-disable-line react-hooks/exhaustive-deps

  // Carica disponibilità aree dalla data selezionata
  useEffect(() => {
    if (!admin?.beachCode || !selectedDate) return;
    getAreaPrenotazioni(admin.beachCode, selectedDate)
      .then(data => setAreaPrenotazioni(data || {}))
      .catch(() => setAreaPrenotazioni({}));
  }, [selectedDate, admin?.beachCode]); // eslint-disable-line react-hooks/exhaustive-deps

  // Carica ordini del giorno selezionato
  useEffect(() => {
    if (!admin?.beachCode || !selectedDate) return;
    setLoadingDailyOrders(true);
    getOrdersByBeachAndDate(admin.beachCode, selectedDate)
      .then(orders => {
        const active = orders.filter(o => o.status !== 'cancelled');
        setDailyOrders(active.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0)));
      })
      .catch(() => setDailyOrders([]))
      .finally(() => setLoadingDailyOrders(false));
  }, [selectedDate, admin?.beachCode]); // eslint-disable-line react-hooks/exhaustive-deps

  // Carica ordini area quando apriamo in modalità area
  useEffect(() => {
    if (drawerMode !== 'area' || !drawerCell || !admin?.beachCode) return;
    setAreaOrders([]);
    setLoadingArea(true);
    const srv = servizi.find(s => s.id === drawerCell.type)
      || servizi.find(s => s.label?.toLowerCase() === drawerCell.servizioLabel?.toLowerCase());
    getOrdersByBeachAndDate(admin.beachCode, selectedDate)
      .then(orders => {
        const active = orders.filter(o =>
          o.status !== 'cancelled' && o.isArea === true &&
          (o.servizioId === drawerCell.type ||
            o.celle?.some(c => c.type === drawerCell.type))
        );
        setAreaOrders(active);
        if (srv?.prezzo) setForm(p => ({ ...p, totalPrice: String(srv.prezzo) }));
      })
      .catch(() => setAreaOrders([]))
      .finally(() => setLoadingArea(false));
  }, [drawerMode, drawerCell, selectedDate, admin?.beachCode]); // eslint-disable-line react-hooks/exhaustive-deps

  // Carica ordine associato quando apriamo in modalità info
  useEffect(() => {
    if (drawerMode !== 'info' || !drawerCell || !admin?.beachCode) return;
    setRelatedOrder(null);
    setLoadingOrder(true);
    getOrdersByBeachAndDate(admin.beachCode, selectedDate)
      .then(orders => {
        const found = orders.find(o =>
          o.status !== 'cancelled' &&
          o.celle?.some(c => c.id === drawerCell.id)
        );
        setRelatedOrder(found || null);
      })
      .catch(() => setRelatedOrder(null))
      .finally(() => setLoadingOrder(false));
  }, [drawerMode, drawerCell, selectedDate, admin?.beachCode]);

  const openDrawer = (cell) => {
    const srv = servizi.find(s => s.id === cell.type)
      || servizi.find(s => s.label?.toLowerCase() === cell.servizioLabel?.toLowerCase());
    const isArea = srv?.tipo === 'area';
    setDrawerCell(cell);
    // Le aree hanno sempre drawerMode 'area' indipendentemente dallo status
    setDrawerMode(isArea ? 'area' : (cell.status === 'free' ? 'book' : 'info'));
    setRelatedOrder(null);
    setAreaOrders([]);
    setAreaQty(1);
    setExtraSel({});
    setForm(p => ({
      ...p, dateType: 'single',
      dateRange: { dal: selectedDate, al: selectedDate },
      dates: [selectedDate], totalPrice: '',
      guestName: '', guestPhone: '', notes: '', seasonalDays: [],
    }));
  };

  const closeDrawer = () => { setDrawerCell(null); setDrawerMode(null); setRelatedOrder(null); setAreaOrders([]); };

  const handleSaveArea = async () => {
    if (!drawerCell) return;
    setSaving(true);
    try {
      const srv = servizi.find(s => s.id === drawerCell.type)
        || servizi.find(s => s.label?.toLowerCase() === drawerCell.servizioLabel?.toLowerCase());
      await createBooking(admin.beachCode, {
        celle: [{ ...drawerCell, quantita: areaQty }],
        guestName: form.guestName,
        guestPhone: form.guestPhone,
        notes: form.notes,
        dateType: form.dateType,
        selectedDate: form.dateType === 'single' ? selectedDate : undefined,
        dateRange: ['range', 'seasonal'].includes(form.dateType) ? form.dateRange : undefined,
        dates: form.dateType === 'multiple' ? form.dates : undefined,
        seasonalDays: form.dateType === 'seasonal' ? form.seasonalDays : undefined,
        totalPrice: Number(form.totalPrice) || (srv?.prezzo || 0) * areaQty,
        quantita: areaQty,
        servizioId: drawerCell.type,
        servizioLabel: srv?.label || drawerCell.servizioLabel,
        isArea: true,
      });
      await refetch();
      closeDrawer();
    } finally { setSaving(false); }
  };

  const handleCancelAreaOrder = async (order) => {
    if (!window.confirm('Cancellare questa prenotazione?')) return;
    setCancelling(true);
    try {
      const areaQuantita =
        order.areaQuantita ||
        (order.servizioId ? { [order.servizioId]: order.celle?.[0]?.quantita || 1 } : {});
      await cancelBookingAndRelease(admin.beachCode, order.id, order.celle || [], order.selectedDate, areaQuantita);
      setAreaOrders(prev => prev.filter(o => o.id !== order.id));
      await refetch();
    } finally { setCancelling(false); }
  };

  const handleSave = async () => {
    if (!drawerCell) return;
    setSaving(true);
    try {
      const params = {
        celle: [drawerCell],
        guestName: form.guestName, guestPhone: form.guestPhone, notes: form.notes,
        dateType: form.dateType,
        selectedDate: form.dateType === 'single' ? selectedDate : undefined,
        dateRange: ['range', 'seasonal'].includes(form.dateType) ? form.dateRange : undefined,
        dates: form.dateType === 'multiple' ? form.dates : undefined,
        seasonalDays: form.dateType === 'seasonal' ? form.seasonalDays : undefined,
        totalPrice: Number(form.totalPrice) || 0,
      };
      if (drawerMode === 'block') await blockSpot(admin.beachCode, params);
      else await createBooking(admin.beachCode, params);
      await refetch();
      closeDrawer();
    } finally { setSaving(false); }
  };

  const handleCancel = async () => {
    if (!relatedOrder) return;
    if (!window.confirm('Sei sicuro di voler cancellare questa prenotazione?')) return;
    setCancelling(true);
    try {
      const areaQuantita =
        relatedOrder.areaQuantita ||
        (relatedOrder.isArea && relatedOrder.servizioId
          ? { [relatedOrder.servizioId]: relatedOrder.celle?.[0]?.quantita || 1 }
          : {});
      await cancelBookingAndRelease(admin.beachCode, relatedOrder.id, relatedOrder.celle || [], relatedOrder.selectedDate, areaQuantita);
      await refetch();
      closeDrawer();
    } finally { setCancelling(false); }
  };

  const inputStyle = {
    background: 'var(--surface)', border: '1px solid var(--border-mid)',
    borderRadius: 'var(--radius-md)', color: '#fff',
    padding: '8px 12px', fontSize: '13px', fontFamily: 'var(--font-sans)', width: '100%',
  };

  const servizio = drawerCell
    ? (servizi.find(s => s.id === drawerCell.type)
      || servizi.find(s => s.label?.toLowerCase() === drawerCell.servizioLabel?.toLowerCase()))
    : null;
  const isAreaServizio = servizio?.tipo === 'area';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Topbar title="Mappa Spiaggia" subtitle={`${Object.keys(availabilityMap).length} date configurate`} />

      <div style={{ padding: isMobile ? '12px 16px' : '24px 28px', overflowY: 'auto', flex: 1 }}>

        {/* Navigazione mese */}
        <div style={{ marginBottom: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '10px' }}>
            <button onClick={prevMonth} style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', color: 'var(--text-soft)', padding: '5px 12px', cursor: 'pointer', fontFamily: 'var(--font-sans)', fontSize: '13px' }}>←</button>
            <div style={{ fontSize: '14px', fontWeight: 500, minWidth: '160px', textAlign: 'center' }}>{MONTHS[viewMonth.month]} {viewMonth.year}</div>
            <button onClick={nextMonth} style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', color: 'var(--text-soft)', padding: '5px 12px', cursor: 'pointer', fontFamily: 'var(--font-sans)', fontSize: '13px' }}>→</button>
          </div>
          <div style={{ display: 'flex', gap: '4px', overflowX: 'auto', paddingBottom: '4px' }}>
            {daysInMonth.map(({ dateStr, day, weekday }) => {
              const isActive = dateStr === selectedDate;
              const isToday = dateStr === today;
              const hasLayout = !!availabilityMap[dateStr];
              const s = getStatsForDate(dateStr);
              const pct = s.total > 0 ? Math.round((s.occupied / s.total) * 100) : 0;
              return (
                <div key={dateStr} onClick={() => setSelectedDate(dateStr)} style={{
                  flexShrink: 0, width: '44px', padding: '7px 4px', borderRadius: 'var(--radius-md)',
                  textAlign: 'center', cursor: 'pointer',
                  border: `1px solid ${isActive ? 'var(--ocean)' : isToday ? 'rgba(245,200,66,0.4)' : 'var(--border)'}`,
                  background: isActive ? 'var(--ocean)' : 'var(--surface)',
                  opacity: hasLayout ? 1 : 0.4, transition: 'all 0.15s',
                }}>
                  <div style={{ fontSize: '9px', color: isActive ? 'rgba(255,255,255,0.7)' : 'var(--text-muted)', textTransform: 'uppercase' }}>{weekday}</div>
                  <div style={{ fontSize: '15px', fontWeight: 500, marginTop: '2px' }}>{day}</div>
                  {hasLayout && pct > 0 && (
                    <div style={{ height: '3px', borderRadius: '2px', marginTop: '4px', background: pct > 80 ? '#f87171' : pct > 50 ? 'var(--sand)' : '#4ade80', width: `${pct}%`, margin: '4px auto 0' }} />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {(loading || Object.keys(availabilityMap).length === 0) ? (
          <div style={{ display: 'flex', justifyContent: 'center', paddingTop: '60px' }}>
            <Spinner size={32} />
          </div>
        ) : layoutCelle.length === 0 ? (
          <Panel><PanelBody>
            <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-muted)', fontSize: '13px' }}>
              🏖️ Nessun layout per questa data.<br />
              <span style={{ fontSize: '12px', marginTop: '8px', display: 'block' }}>
                Pubblica il layout dal <strong style={{ color: 'var(--text-soft)' }}>Beach Builder</strong>.
              </span>
            </div>
          </PanelBody></Panel>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 260px', gap: '16px' }}>
            <Panel>
              <PanelHead title="Layout spiaggia" subtitle={`${selectedDate} · clicca un posto per gestirlo`} />
              <PanelBody style={{ padding: 0 }}>
                {/* Sfondo sabbia realistico */}
                <div style={{ background: '#e8d5a3', borderRadius: 'var(--radius-md)', overflow: 'hidden', position: 'relative' }}>
                  {/* Texture sabbia */}
                  <div style={{
                    position: 'absolute', inset: 0, pointerEvents: 'none',
                    backgroundImage: 'radial-gradient(circle, rgba(160,130,80,0.12) 1px, transparent 1px)',
                    backgroundSize: '8px 8px', zIndex: 0
                  }} />
                  {/* Mare */}
                  <div style={{
                    height: '40px', background: 'linear-gradient(180deg, #1a7fa8 0%, #3ab5c3 50%, #5dccd6 80%, #a8e4e8 100%)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', zIndex: 1
                  }}>
                    <span style={{ fontSize: '10px', color: 'rgba(255,255,255,0.65)', letterSpacing: '0.15em' }}>〰 MARE 〰</span>
                  </div>
                  <svg viewBox="0 0 800 16" style={{ display: 'block', marginTop: '-1px', position: 'relative', zIndex: 1 }} preserveAspectRatio="none" height="14" width="100%">
                    <path d="M0 8 Q200 0 400 8 Q600 16 800 8 L800 16 L0 16Z" fill="#e8d5a3" />
                  </svg>
                  {/* Griglia */}
                  <div style={{ padding: '8px 12px 12px', position: 'relative', zIndex: 1, display: 'flex', justifyContent: 'center' }}>
                    <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                      <div style={{ display: 'inline-flex', flexDirection: 'column', gap: '4px' }}>
                        {rows.map(({ rowIdx, label, cells }) => (
                          <div key={`row-${rowIdx}`} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <div style={{ width: '20px', fontSize: '11px', color: 'var(--text-muted)', textAlign: 'center', flexShrink: 0 }}>{label}</div>
                            {cells.map((cell, cellIdx) => {
                              // Lookup servizio: prima per id, poi per label (servizi legacy)
                              const srv = servizi.find(s => s.id === cell.type)
                                || servizi.find(s => s.label?.toLowerCase() === cell.servizioLabel?.toLowerCase());
                              const isPath = cell.type === 'percorso';
                              const isSpiaggia = cell.type === 'spiaggia';
                              const isTree = cell.type === 'albero';
                              const isStaticDeco = isPath || isSpiaggia || isTree;
                              // Prenotabile = servizio con prenotabile:true, o servizio non trovato ma non decorazione statica
                              const isBookable = !isStaticDeco && (srv ? srv.prenotabile !== false : false);
                              const isDecoration = isStaticDeco || (srv && srv.prenotabile === false);
                              const st = STATUS_STYLE[cell.status] || STATUS_STYLE.free;
                              const isSelected = drawerCell?.id === cell.id;
                              const cellW = isMobile ? 40 : 36;
                              const iconName = resolveIcon(cell, srv);
                              const colore = resolveColore(cell, srv);
                              const iconColor = isSelected ? 'rgba(100,70,0,0.9)'
                                : isTree ? 'rgba(56,142,60,0.85)'
                                  : isPath ? 'rgba(120,90,50,0.4)'
                                    : isDecoration ? `${colore}bb`
                                      : cell.status === 'occupied' ? 'rgba(224,100,60,0.9)'
                                        : '#e65100';  // libero e prenotato: stesso arancione app
                              return (
                                <div key={cell.id ?? `${rowIdx}-${cellIdx}`} onClick={() => { if (isBookable || (!isStaticDeco && srv)) openDrawer(cell); }}
                                  title={`${cell.id} · ${srv?.label || cell.servizioLabel || cell.type} · ${cell.status}`}
                                  style={{
                                    width: `${cellW}px`, height: `${cellW}px`,
                                    borderRadius: isTree ? '50%' : isPath ? '3px' : '7px',
                                    background: isSelected ? 'rgba(245,200,66,0.9)'
                                      : isPath ? 'rgba(180,155,100,0.25)'
                                        : isSpiaggia ? 'rgba(245,200,66,0.2)'
                                          : isTree ? 'rgba(56,142,60,0.18)'
                                            : isDecoration ? `${colore}18`   // strutture non prenotabili: colore tenue
                                              : st.bg,
                                    border: isSelected ? '2px solid #e6b800'
                                      : isPath ? '1px dashed rgba(150,120,70,0.3)'
                                        : isSpiaggia ? '1px solid rgba(245,200,66,0.35)'
                                          : isTree ? '1px solid rgba(56,142,60,0.3)'
                                            : isDecoration ? `1px solid ${colore}35`
                                              : `1px solid ${st.border}`,
                                    display: 'flex', flexDirection: 'column',
                                    alignItems: 'center', justifyContent: 'center',
                                    cursor: isBookable ? 'pointer' : 'default',
                                    flexShrink: 0, transition: 'transform 0.1s',
                                    position: 'relative', overflow: 'hidden',
                                    // decorazioni più opache per non distogliere l'attenzione
                                    opacity: isDecoration && !isSelected ? 0.65 : 1,
                                    boxShadow: isBookable && cell.status === 'free'
                                      ? '0 1px 3px rgba(0,0,0,0.12), inset 0 1px 0 rgba(255,255,255,0.8)' : 'none',
                                  }}
                                  onMouseEnter={e => { if (isBookable) e.currentTarget.style.transform = 'scale(1.1)'; else e.currentTarget.style.opacity = isDecoration ? '0.85' : '1'; }}
                                  onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; if (isDecoration) e.currentTarget.style.opacity = '0.65'; }}
                                >
                                  {!isSpiaggia && (iconName
                                    ? <span className="material-symbols-outlined" style={{
                                      fontSize: '15px',
                                      lineHeight: 1,
                                      width: '15px',
                                      height: '15px',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      color: iconColor,
                                      fontVariationSettings: "'FILL' 0, 'wght' 300, 'GRAD' 0, 'opsz' 24",
                                      userSelect: 'none',
                                      flexShrink: 0,
                                    }}>{iconName}</span>
                                    : isBookable
                                      ? <div style={{ width: cellW > 36 ? 12 : 10, height: cellW > 36 ? 12 : 10, borderRadius: '50%', background: colore }} />
                                      : null
                                  )}
                                  {isBookable && (
                                    <span style={{
                                      fontSize: '7px', fontWeight: 600, marginTop: '1px', lineHeight: 1,
                                      color: isSelected ? 'rgba(100,70,0,0.8)'
                                        : cell.status === 'occupied' ? 'rgba(224,100,60,0.7)'
                                          : 'rgba(0,0,0,0.3)',
                                      maxWidth: `${cellW - 4}px`,
                                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                    }}>{cell.id}</span>
                                  )}
                                  {isBookable && cell.status === 'reserved' && !isSelected && (
                                    <div style={{
                                      position: 'absolute', top: '2px', right: '2px',
                                      width: '5px', height: '5px', borderRadius: '50%', background: '#F5C842',
                                    }} />
                                  )}
                                  {isBookable && cell.status === 'occupied' && !isSelected && (
                                    <div style={{
                                      position: 'absolute', top: '2px', right: '2px',
                                      width: '5px', height: '5px', borderRadius: '50%', background: 'rgba(224,100,60,0.9)',
                                    }} />
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        ))}
                      </div>
                    </div>{/* end overflow wrapper */}
                    {/* Striscia sabbia bassa */}
                    <div style={{ height: '16px', background: 'rgba(160,130,80,0.08)', margin: '4px 0 0' }} />
                  </div>{/* end padding */}
                </div>{/* end sand container */}
                <div style={{ display: 'flex', gap: '14px', marginTop: '12px', flexWrap: 'wrap' }}>
                  {[
                    { color: 'rgba(220,210,195,0.9)', label: `Libero (${stats.free})` },
                    { color: '#F5C842', label: `Prenotato (${stats.reserved ?? tariffe?.length ?? ''})` },
                    { color: 'rgba(224,100,60,0.8)', label: `Occupato (${stats.occupied})` },
                    { color: 'rgba(150,120,70,0.5)', label: 'Bloccato' },
                  ].map(({ color, label }) => (
                    <div key={label} style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '12px', color: 'var(--text-muted)' }}>
                      <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: color }} />{label}
                    </div>
                  ))}
                </div>
              </PanelBody>
            </Panel>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <Panel>
                <PanelHead title="Riepilogo" subtitle={selectedDate} />
                <PanelBody style={{ padding: '12px 20px' }}>
                  {[
                    { label: 'Totale posti', value: stats.total, color: 'var(--text-primary)' },
                    { label: 'Liberi', value: stats.free, color: '#4ade80' },
                    { label: 'Occupati', value: stats.occupied, color: '#f87171' },
                    { label: 'Occupazione', value: stats.total > 0 ? `${Math.round((stats.occupied / stats.total) * 100)}%` : '—', color: 'var(--sand)' },
                  ].map(row => (
                    <div key={row.label} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--border)', fontSize: '13px' }}>
                      <span style={{ color: 'var(--text-muted)' }}>{row.label}</span>
                      <span style={{ fontWeight: 500, color: row.color }}>{row.value}</span>
                    </div>
                  ))}
                </PanelBody>
              </Panel>
              <Panel>
                <PanelHead title="Occupazione mensile" />
                <PanelBody style={{ padding: '12px 20px' }}>
                  {daysInMonth.filter(d => availabilityMap[d.dateStr]).slice(0, 12).map(d => {
                    const s = getStatsForDate(d.dateStr);
                    const pct = s.total > 0 ? (s.occupied / s.total) * 100 : 0;
                    return (
                      <div key={d.dateStr} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                        <span style={{ fontSize: '11px', color: 'var(--text-muted)', width: '24px', flexShrink: 0 }}>{d.day}</span>
                        <div style={{ flex: 1, height: '5px', background: 'var(--surface)', borderRadius: '3px', overflow: 'hidden' }}>
                          <div style={{ height: '100%', borderRadius: '3px', width: `${pct}%`, background: pct > 80 ? '#f87171' : 'var(--ocean)' }} />
                        </div>
                        <span style={{ fontSize: '11px', color: 'var(--text-muted)', width: '28px', textAlign: 'right' }}>{Math.round(pct)}%</span>
                      </div>
                    );
                  })}
                </PanelBody>
              </Panel>
              {/* Prenotazioni del giorno */}
              <Panel>
                <PanelHead
                  title="Prenotazioni del giorno"
                  subtitle={`${dailyOrders.length} attiv${dailyOrders.length === 1 ? 'a' : 'e'}`}
                />
                <PanelBody style={{ padding: '8px 12px' }}>
                  {loadingDailyOrders ? (
                    <div style={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
                      <Spinner size={20} />
                    </div>
                  ) : dailyOrders.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '16px', fontSize: '12px', color: 'var(--text-muted)' }}>
                      Nessuna prenotazione
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      {dailyOrders.map(order => {
                        const celle = order.celle || [];
                        const celleLabel = celle.length > 0
                          ? celle.map(c => c.id).join(', ')
                          : order.servizioLabel || '—';
                        const statusColor = order.status === 'confirmed' ? '#4ade80'
                          : order.status === 'blocked' ? 'var(--text-muted)'
                            : 'var(--sand)';
                        return (
                          <div key={order.id} style={{
                            background: 'var(--surface)', border: '1px solid var(--border)',
                            borderRadius: 'var(--radius-md)', padding: '8px 10px',
                          }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontSize: '12.5px', fontWeight: 500, color: 'var(--text-soft)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                  {order.guestName || 'Ospite'}
                                </div>
                                <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '2px' }}>
                                  {celleLabel}
                                  {order.guestPhone && ` · ${order.guestPhone}`}
                                </div>
                              </div>
                              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', flexShrink: 0, marginLeft: '8px' }}>
                                <span style={{ fontSize: '12px', fontWeight: 600, color: 'var(--sand)' }}>
                                  {order.totalPrice > 0 ? `€ ${order.totalPrice}` : '—'}
                                </span>
                                <div style={{ width: '7px', height: '7px', borderRadius: '50%', background: statusColor, marginTop: '4px' }} />
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </PanelBody>
              </Panel>
            </div>
          </div>
        )}
      </div>

      {/* ── Drawer ── */}
      {drawerCell && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 50, display: 'flex', justifyContent: 'flex-end' }} onClick={closeDrawer}>
          <div onClick={e => e.stopPropagation()} style={{
            width: isMobile ? '100%' : '400px', height: '100%', background: 'var(--navy-mid)',
            borderLeft: isMobile ? 'none' : '1px solid var(--border)',
            display: 'flex', flexDirection: 'column',
            boxShadow: isMobile ? 'none' : '-8px 0 32px rgba(0,0,0,0.4)',
          }}>

            {/* Header */}
            <div style={{ padding: '20px 24px 16px', borderBottom: '1px solid var(--border)', flexShrink: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                <div>
                  <div style={{ fontSize: '16px', fontWeight: 500 }}>
                    {servizio?.label || drawerCell.servizioLabel || drawerCell.type}
                    {!isAreaServizio && <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}> {drawerCell.id}</span>}
                    {isAreaServizio && <span style={{ marginLeft: '8px', fontSize: '11px', background: 'rgba(31,147,224,0.15)', color: 'var(--ocean)', padding: '2px 8px', borderRadius: '20px', verticalAlign: 'middle' }}>area</span>}
                  </div>
                  <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '2px' }}>
                    {selectedDate} · {
                      drawerCell.status === 'free' ? 'Disponibile' :
                        drawerCell.status === 'occupied' ? 'Prenotato' :
                          drawerCell.status === 'blocked' ? 'Bloccato' : 'Prenotato'
                    }
                  </div>
                </div>
                <button onClick={closeDrawer} style={{ background: 'transparent', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: '20px' }}>×</button>
              </div>
              {drawerCell.status === 'free' && !isAreaServizio && (
                <div style={{ display: 'flex', gap: '6px' }}>
                  <ModeButton id="book" label="📋 Prenota" drawerMode={drawerMode} setDrawerMode={setDrawerMode} />
                  <ModeButton id="block" label="🔒 Blocca" drawerMode={drawerMode} setDrawerMode={setDrawerMode} />
                </div>
              )}
            </div>

            {/* ── AREA: gestione area lettini ── */}
            {drawerMode === 'area' && (
              <div style={{ padding: '20px 24px', flex: 1, display: 'flex', flexDirection: 'column', gap: '14px', overflowY: 'auto' }}>

                {/* Contatore disponibilità */}
                {(() => {
                  const cap = servizio?.capacitaMax || 0;
                  // Legge da areaPrenotazioni (campo diretto in disponibilita)
                  const prenotati = areaPrenotazioni[drawerCell.type] ?? areaOrders.reduce((sum, o) => sum + (o.areaQuantita?.[drawerCell.type] || o.quantita || 1), 0);
                  const disponibili = Math.max(0, cap - prenotati);
                  const pct = cap > 0 ? Math.round((prenotati / cap) * 100) : 0;
                  return (
                    <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', padding: '14px 16px' }}>
                      <div style={{ fontSize: '11px', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '10px' }}>
                        Disponibilità
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ fontSize: '22px', fontWeight: 500, color: disponibili > 0 ? '#4ade80' : '#f87171' }}>{disponibili}</div>
                          <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>disponibili</div>
                        </div>
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ fontSize: '22px', fontWeight: 500, color: 'var(--sand)' }}>{prenotati}</div>
                          <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>prenotati</div>
                        </div>
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ fontSize: '22px', fontWeight: 500 }}>{cap}</div>
                          <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>capienza</div>
                        </div>
                      </div>
                      <div style={{ height: '6px', background: 'var(--navy)', borderRadius: '3px', overflow: 'hidden' }}>
                        <div style={{ height: '100%', borderRadius: '3px', width: `${pct}%`, background: pct > 80 ? '#f87171' : pct > 50 ? 'var(--sand)' : '#4ade80', transition: 'width 0.3s' }} />
                      </div>
                    </div>
                  );
                })()}

                {/* Lista prenotazioni esistenti */}
                {loadingArea ? (
                  <div style={{ display: 'flex', justifyContent: 'center', padding: '16px' }}><Spinner size={20} /></div>
                ) : areaOrders.length > 0 ? (
                  <div>
                    <div style={{ fontSize: '11px', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '8px' }}>
                      Prenotazioni attive
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      {areaOrders.map(o => (
                        <div key={o.id} style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', padding: '10px 12px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: '13px', fontWeight: 500, color: 'var(--text-soft)' }}>{o.guestName || 'Cliente'}</div>
                            <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '2px' }}>
                              {o.quantita || 1} lettino{(o.quantita || 1) > 1 ? 'i' : ''} · € {o.totalPrice || '—'}
                              {o.guestPhone && ` · ${o.guestPhone}`}
                            </div>
                          </div>
                          <button onClick={() => handleCancelAreaOrder(o)} style={{
                            padding: '4px 10px', borderRadius: 'var(--radius-md)', fontSize: '11px',
                            background: 'rgba(231,76,60,0.1)', border: '1px solid rgba(231,76,60,0.3)',
                            color: '#f87171', cursor: 'pointer', fontFamily: 'var(--font-sans)', flexShrink: 0,
                          }}>
                            Cancella
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div style={{ fontSize: '12px', color: 'var(--text-muted)', textAlign: 'center', padding: '8px 0' }}>
                    Nessuna prenotazione per questa data
                  </div>
                )}

                {/* Form nuova prenotazione area */}
                {(() => {
                  const cap = servizio?.capacitaMax || 0;
                  const prenotati = areaPrenotazioni[drawerCell.type] ?? areaOrders.reduce((sum, o) => sum + (o.areaQuantita?.[drawerCell.type] || o.quantita || 1), 0);
                  const disponibili = Math.max(0, cap - prenotati);
                  if (disponibili === 0) return (
                    <div style={{ fontSize: '12px', color: '#f87171', textAlign: 'center', padding: '8px', background: 'rgba(231,76,60,0.08)', borderRadius: 'var(--radius-md)', border: '1px solid rgba(231,76,60,0.2)' }}>
                      Area al completo per questa data
                    </div>
                  );
                  return (
                    <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      <div style={{ fontSize: '11px', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                        Nuova prenotazione
                      </div>
                      <div>
                        <label style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block', marginBottom: '5px' }}>Nome cliente</label>
                        <input placeholder="Mario Rossi" value={form.guestName} onChange={setF('guestName')}
                          style={{ background: 'var(--navy)', border: '1px solid var(--border-mid)', borderRadius: 'var(--radius-md)', color: '#fff', padding: '8px 12px', fontSize: '13px', fontFamily: 'var(--font-sans)', width: '100%' }} />
                      </div>
                      <div>
                        <label style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block', marginBottom: '5px' }}>Telefono (opz.)</label>
                        <input placeholder="+39 333 1234567" value={form.guestPhone} onChange={setF('guestPhone')}
                          style={{ background: 'var(--navy)', border: '1px solid var(--border-mid)', borderRadius: 'var(--radius-md)', color: '#fff', padding: '8px 12px', fontSize: '13px', fontFamily: 'var(--font-sans)', width: '100%' }} />
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                        <div>
                          <label style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block', marginBottom: '5px' }}>
                            Quantità (max {disponibili})
                          </label>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <button onClick={() => setAreaQty(q => Math.max(1, q - 1))} style={{ width: '28px', height: '28px', borderRadius: '6px', border: '1px solid var(--border)', background: 'var(--navy)', color: '#fff', cursor: 'pointer', fontSize: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>−</button>
                            <span style={{ minWidth: '24px', textAlign: 'center', fontSize: '15px', fontWeight: 500 }}>{areaQty}</span>
                            <button onClick={() => setAreaQty(q => Math.min(disponibili, q + 1))} style={{ width: '28px', height: '28px', borderRadius: '6px', border: '1px solid var(--border)', background: 'var(--navy)', color: '#fff', cursor: 'pointer', fontSize: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>+</button>
                          </div>
                        </div>
                        <div>
                          <label style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block', marginBottom: '5px' }}>
                            Totale <span style={{ color: 'var(--ocean)' }}>€ {(servizio?.prezzo || 0)} × {areaQty}</span>
                          </label>
                          <div style={{ position: 'relative' }}>
                            <span style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: 'var(--sand)', fontSize: '13px' }}>€</span>
                            <input type="number" min="0" step="0.5"
                              value={form.totalPrice || (servizio?.prezzo || 0) * areaQty}
                              onChange={setF('totalPrice')}
                              style={{ background: 'var(--navy)', border: '1px solid var(--border-mid)', borderRadius: 'var(--radius-md)', color: '#fff', padding: '8px 12px', paddingLeft: '24px', fontSize: '13px', fontFamily: 'var(--font-sans)', width: '100%' }} />
                          </div>
                        </div>
                      </div>
                      <button onClick={handleSaveArea} disabled={saving} style={{
                        width: '100%', padding: '10px', borderRadius: 'var(--radius-md)',
                        background: saving ? 'rgba(31,147,224,0.4)' : 'var(--ocean)',
                        border: 'none', color: '#fff', fontSize: '13px', fontWeight: 500,
                        cursor: saving ? 'default' : 'pointer', fontFamily: 'var(--font-sans)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
                      }}>
                        {saving ? <Spinner size={14} /> : `✓ Prenota ${areaQty} lettino${areaQty > 1 ? 'i' : ''}`}
                      </button>
                    </div>
                  );
                })()}
              </div>
            )}

            {/* ── INFO: dati prenotazione esistente ── */}
            {drawerMode === 'info' && (
              <div style={{ padding: '20px 24px', flex: 1, display: 'flex', flexDirection: 'column', gap: '14px', overflowY: 'auto' }}>
                {loadingOrder ? (
                  <div style={{ display: 'flex', justifyContent: 'center', padding: '24px' }}><Spinner size={24} /></div>
                ) : relatedOrder ? (
                  <>
                    <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', padding: '14px 16px' }}>
                      <div style={{ fontSize: '11px', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '10px' }}>
                        Dettagli prenotazione
                      </div>
                      {[
                        ['Cliente', relatedOrder.guestName || '—'],
                        ['Telefono', relatedOrder.guestPhone || '—'],
                        ['Totale', relatedOrder.totalPrice ? `€ ${relatedOrder.totalPrice}` : '—'],
                        ['Date', relatedOrder.selectedDate?.slice(0, 3).join(', ') + (relatedOrder.selectedDate?.length > 3 ? ` +${relatedOrder.selectedDate.length - 3}` : '') || '—'],
                        ['Note', relatedOrder.notes || '—'],
                        ['ID ordine', relatedOrder.id?.slice(0, 10) + '…'],
                      ].map(([label, value]) => (
                        <div key={label} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid var(--border)', fontSize: '13px', gap: '12px' }}>
                          <span style={{ color: 'var(--text-muted)', flexShrink: 0 }}>{label}</span>
                          <span style={{ color: 'var(--text-soft)', fontWeight: 500, textAlign: 'right', wordBreak: 'break-word' }}>{value}</span>
                        </div>
                      ))}
                    </div>

                    <button onClick={handleCancel} disabled={cancelling} style={{
                      width: '100%', padding: '10px', borderRadius: 'var(--radius-md)',
                      background: 'rgba(231,76,60,0.12)', border: '1px solid rgba(231,76,60,0.35)',
                      color: '#f87171', fontSize: '13px', cursor: cancelling ? 'default' : 'pointer',
                      fontFamily: 'var(--font-sans)', display: 'flex', alignItems: 'center',
                      justifyContent: 'center', gap: '6px', opacity: cancelling ? 0.6 : 1,
                    }}>
                      {cancelling ? <Spinner size={14} /> : '🗑 Cancella prenotazione'}
                    </button>
                  </>
                ) : (
                  <div style={{ fontSize: '12px', color: 'var(--text-muted)', padding: '14px', background: 'var(--surface)', borderRadius: 'var(--radius-md)', border: '1px solid var(--border)', lineHeight: 1.6 }}>
                    Posto segnato come <strong style={{ color: 'var(--text-soft)' }}>{drawerCell.status}</strong> ma nessun ordine trovato per questa data.<br />
                    Potrebbe essere stato prenotato dall'app mobile o da un altro canale.
                  </div>
                )}
              </div>
            )}

            {/* ── FORM: prenota / blocca ── */}
            {(drawerMode === 'book' || drawerMode === 'block') && (
              <div style={{ padding: '20px 24px', flex: 1, display: 'flex', flexDirection: 'column', gap: '16px', overflowY: 'auto' }}>

                <div>
                  <label style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                    Tipo di prenotazione
                  </label>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px' }}>
                    {DATE_TYPES.map(dt => (
                      <button key={dt.id} onClick={() => setForm(p => ({ ...p, dateType: dt.id }))} style={{
                        padding: '8px 10px', borderRadius: 'var(--radius-md)', fontSize: '12px',
                        border: `1px solid ${form.dateType === dt.id ? 'var(--ocean)' : 'var(--border)'}`,
                        background: form.dateType === dt.id ? 'rgba(31,147,224,0.12)' : 'var(--surface)',
                        color: form.dateType === dt.id ? 'var(--ocean)' : 'var(--text-soft)',
                        cursor: 'pointer', fontFamily: 'var(--font-sans)', textAlign: 'left',
                      }}>
                        {dt.icon} {dt.label}
                      </button>
                    ))}
                  </div>
                </div>

                {form.dateType === 'single' && (
                  <div>
                    <label style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block', marginBottom: '6px' }}>Data</label>
                    <div style={{ ...inputStyle, padding: '9px 12px', color: 'var(--text-soft)' }}>{selectedDate}</div>
                  </div>
                )}

                {(form.dateType === 'range' || form.dateType === 'seasonal') && (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                    {[['Dal', 'dal'], ['Al', 'al']].map(([lbl, key]) => (
                      <div key={key}>
                        <label style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block', marginBottom: '5px' }}>{lbl}</label>
                        <input type="date" value={form.dateRange[key]}
                          onChange={e => setForm(p => ({ ...p, dateRange: { ...p.dateRange, [key]: e.target.value } }))}
                          style={inputStyle} />
                      </div>
                    ))}
                  </div>
                )}

                {form.dateType === 'seasonal' && (
                  <div>
                    <label style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                      Giorni della settimana
                    </label>
                    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                      {WEEKDAYS_FULL.map((wd, i) => {
                        const active = form.seasonalDays.includes(i);
                        return (
                          <button key={i} onClick={() => setForm(p => ({ ...p, seasonalDays: active ? p.seasonalDays.filter(d => d !== i) : [...p.seasonalDays, i] }))} style={{
                            padding: '5px 10px', borderRadius: '20px', fontSize: '11.5px',
                            border: `1px solid ${active ? 'var(--ocean)' : 'var(--border)'}`,
                            background: active ? 'rgba(31,147,224,0.15)' : 'transparent',
                            color: active ? 'var(--ocean)' : 'var(--text-muted)',
                            cursor: 'pointer', fontFamily: 'var(--font-sans)',
                          }}>{wd}</button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {form.dateType === 'multiple' && (
                  <div>
                    <label style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block', marginBottom: '6px' }}>Aggiungi date</label>
                    <input type="date" onChange={e => {
                      const d = e.target.value;
                      if (d && !form.dates.includes(d)) setForm(p => ({ ...p, dates: [...p.dates, d].sort() }));
                      e.target.value = '';
                    }} style={inputStyle} />
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px', marginTop: '8px' }}>
                      {form.dates.map(d => (
                        <div key={d} style={{ padding: '3px 10px', borderRadius: '20px', fontSize: '11.5px', background: 'rgba(31,147,224,0.15)', border: '1px solid rgba(31,147,224,0.3)', color: 'var(--ocean)', display: 'flex', alignItems: 'center', gap: '5px' }}>
                          {d}
                          <span onClick={() => setForm(p => ({ ...p, dates: p.dates.filter(x => x !== d) }))} style={{ cursor: 'pointer', fontSize: '13px', lineHeight: 1 }}>×</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {drawerMode === 'book' && (
                  <>
                    <div style={{ height: '1px', background: 'var(--border)' }} />
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      <div>
                        <label style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block', marginBottom: '5px' }}>Nome cliente</label>
                        <input placeholder="Mario Rossi" value={form.guestName} onChange={setF('guestName')} style={inputStyle} />
                      </div>
                      <div>
                        <label style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block', marginBottom: '5px' }}>Telefono (opzionale)</label>
                        <input placeholder="+39 333 1234567" value={form.guestPhone} onChange={setF('guestPhone')} style={inputStyle} />
                      </div>
                    </div>

                    {/* Extra opzionali */}
                    {(servizio?.extra || []).some(e => e.attivo) && (
                      <>
                        <div style={{ height: '1px', background: 'var(--border)' }} />
                        <div>
                          <label style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                            Extra opzionali
                          </label>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                            {(servizio.extra || []).filter(e => e.attivo).map(e => {
                              const qty = extraSel[e.id] || 0;
                              const checked = qty > 0;
                              return (
                                <div key={e.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', padding: '8px 10px' }}>
                                  <input
                                    type="checkbox"
                                    checked={checked}
                                    onChange={ev => setExtraSel(s => ({ ...s, [e.id]: ev.target.checked ? 1 : 0 }))}
                                    style={{ width: '14px', height: '14px', accentColor: 'var(--ocean)', cursor: 'pointer', flexShrink: 0 }}
                                  />
                                  <span style={{ flex: 1, fontSize: '12.5px', color: checked ? 'var(--text-soft)' : 'var(--text-muted)' }}>{e.label}</span>
                                  {checked && (
                                    <input
                                      type="number"
                                      min="1"
                                      max={e.massimo || undefined}
                                      value={qty}
                                      onChange={ev => setExtraSel(s => ({ ...s, [e.id]: Math.max(1, Math.min(parseInt(ev.target.value) || 1, e.massimo || 999)) }))}
                                      style={{ ...inputStyle, width: '52px', padding: '4px 8px', textAlign: 'center' }}
                                    />
                                  )}
                                  <span style={{ fontSize: '12px', color: 'var(--sand)', flexShrink: 0, minWidth: '44px', textAlign: 'right' }}>
                                    {checked ? `€ ${(qty * e.prezzo).toFixed(2)}` : `+ € ${e.prezzo}`}
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </>
                    )}

                    <div>
                      <label style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block', marginBottom: '5px' }}>
                        Prezzo totale
                        {prezzoSuggerito !== null && Object.values(extraSel).every(q => q === 0) && (
                          <span style={{ color: 'var(--ocean)', marginLeft: '6px' }}>(tariffa: € {prezzoSuggerito})</span>
                        )}
                      </label>
                      <div style={{ position: 'relative' }}>
                        <span style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: 'var(--sand)', fontSize: '13px' }}>€</span>
                        <input type="number" min="0" step="0.5" placeholder={prezzoSuggerito || '0'}
                          value={form.totalPrice} onChange={setF('totalPrice')}
                          style={{ ...inputStyle, paddingLeft: '24px' }} />
                      </div>
                    </div>
                  </>
                )}

                <div>
                  <label style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block', marginBottom: '5px' }}>
                    {drawerMode === 'block' ? 'Motivo blocco (opzionale)' : 'Note (opzionale)'}
                  </label>
                  <textarea rows={2}
                    placeholder={drawerMode === 'block' ? 'es. Manutenzione, riservato VIP...' : 'es. Cliente abituale, allergie...'}
                    value={form.notes} onChange={setF('notes')}
                    style={{ ...inputStyle, resize: 'vertical', minHeight: '60px' }} />
                </div>
              </div>
            )}

            {/* Footer */}
            {(drawerMode === 'book' || drawerMode === 'block') && (
              <div style={{ padding: '16px 24px', borderTop: '1px solid var(--border)', display: 'flex', gap: '8px', flexShrink: 0 }}>
                <Button variant={drawerMode === 'block' ? 'ghost' : 'primary'} onClick={handleSave} disabled={saving} fullWidth
                  style={drawerMode === 'block' ? { background: 'rgba(120,120,120,0.2)', borderColor: 'rgba(120,120,120,0.4)', color: '#fff' } : {}}>
                  {saving ? <Spinner size={14} /> : drawerMode === 'block' ? '🔒 Blocca posto' : '✓ Conferma prenotazione'}
                </Button>
                <Button variant="ghost" onClick={closeDrawer}>Annulla</Button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function ModeButton({ id, label, drawerMode, setDrawerMode }) {
  const active = drawerMode === id;
  return (
    <button onClick={() => setDrawerMode(active ? null : id)} style={{
      padding: '6px 14px', borderRadius: 'var(--radius-md)', fontSize: '12.5px',
      border: `1px solid ${active ? 'var(--ocean)' : 'var(--border-mid)'}`,
      background: active ? 'rgba(31,147,224,0.15)' : 'transparent',
      color: active ? 'var(--ocean)' : 'var(--text-soft)',
      cursor: 'pointer', fontFamily: 'var(--font-sans)',
    }}>
      {label}
    </button>
  );
}